/* SpawnAnything — betting mode, pure logic: strength estimate, odds, close matchups and the coin wallet.
   No DOM, no three.js: runs in the browser and in Node tests.

   Strength estimate = a tiny aggregate battle (Lanchester style, a few hundred steps on two HP pools):
   approach under fire, melee frontage (only units touching the enemy blob can hit), ranged band, area hits
   by crowd density, overkill (a T-Rex bite on a chicken only removes one chicken), accuracy, stuns and knockback.
   The odds come from how far one army's head count would have to move to make the duel even.
   Constants are calibrated against the real headless simulation (tests/bet.test.js checks a few battles). */

/* ---------------- unit stats (mirror of buildArmy in src/game/voxel.js, without building any mesh) ---------------- */
export function unitStats(ty, wp, size = 1, count = 1) {
  const s = size, r = ty.r * s;
  return {
    count, s, r, speed: ty.speed * Math.sqrt(s), hp: ty.hp * s * s * s, mass: ty.mass * s * s,
    melee: !!wp.melee, area: !!(wp.cleave || ty.stomp || s >= 3), cleave: !!wp.cleave, stomp: !!ty.stomp,
    reach: (wp.reach || 0) * s, range: (wp.range || 0) * (1 + (s - 1) * 0.25),
    dmg: wp.dmg * ty.dmgMul * Math.pow(s, 1.5), cd: wp.cd, hitscan: !!wp.hitscan, acc: wp.acc || 1,
    proj: wp.proj || 0, aoe: (wp.aoe || 0) * (1 + (s - 1) * 0.35),
    knock: (ty.stomp ? Math.max(wp.knock, 6) : wp.knock) * Math.sqrt(s), giant: r > 1.95, bravery: s >= 3 ? 9 : ty.bravery,
  };
}

/* ---------------- aggregate duel ---------------- */
export const MODEL = {
  crowd: 0.84,      // share of an area blast that is actually filled with enemies
  falloff: 0.72,    // mean blast damage (1 at the centre, 0.45 at the edge)
  projHit: 0.8,     // single-target projectiles that connect (moving targets, spread)
  splashHit: 0.92,  // area projectiles that land close enough
  wrap: 0.49,       // how much of the enemy blob's rim melee attackers get to use
  band: 2,          // depth of the firing band as a share of the weapon range (shooters fan out)
  walk: 0.57,       // effective approach speed (crowding, detours)
  stun: 0.1,        // marching time lost per hit taken (0.22 s at 30 % speed)
  shove: 3.5,       // weight of the walk back after a knockback (shoved units also lose their place)
  dt: 0.2, tMax: 900,
};
const PI = Math.PI;
const cellOf = S => (2 * S.r * 1.1) ** 2;                    // ground area one unit occupies in a crowd
const formationWidth = S => Math.ceil(Math.sqrt(S.count * 2.2)) * (S.r * 2.44 + 0.15);
const attackDist = (S, E) => (S.melee ? S.reach : S.range) + S.r + E.r;

function prep(S, E, M) {
  const aE = cellOf(E);
  let hitsMax = 1, f = 1, chance = 1, up = S.melee ? 0.5 : S.hitscan ? 0.3 : 0.95;
  if (S.melee && S.area) {
    const R = S.reach + S.r * 0.8 + E.r; f = M.falloff; up = 0.75;
    // the sim gives every sweep a damage budget (sim.worker.js resolveMelee): it ends after budget / (damage per target) targets
    const budget = S.dmg * (S.cleave || S.stomp ? 3 : 1) * S.s;
    hitsMax = Math.min(M.crowd * 0.55 * PI * Math.max(0, R * R - S.r * S.r) / aE, budget / Math.max(1, Math.min(S.dmg * f, E.hp)));
  }
  else if (!S.melee && S.aoe > 0) { hitsMax = M.crowd * PI * S.aoe * S.aoe / aE; f = M.falloff; chance = S.hitscan ? S.acc : M.splashHit; }
  else if (!S.melee) chance = S.hitscan ? S.acc : M.projHit;
  // what one hit does to an enemy besides damage: stun, shove and toss (giants shrug it off)
  let loseAtk = 0, loseMove = 0;
  if (!E.giant) {
    const kv = S.knock * f * up / E.mass, air = kv > 2.2 ? 2 * Math.min(17, kv) / 24 : 0;
    const back = Math.min(22, S.knock * f / E.mass) / 7 / E.speed * M.shove;
    loseAtk = E.melee ? air + back : air; loseMove = M.stun + air + back;
  }
  return {
    hitsMax: Math.max(1, hitsMax), perHit: Math.min(S.dmg * f, E.hp), rate: chance / S.cd, loseAtk, loseMove,
    dist: attackDist(S, E), ranks: 1 + S.reach / (2 * S.r * 1.1), slot: 2 * S.r * 1.1,
    band: Math.max(S.r * 2, (S.range || 0) * M.band) / cellOf(S),
  };
}
// attackers that can reach the enemy right now
function engaged(S, P, n, nE, E, width, M) {
  if (n <= 0) return 0;
  if (S.melee) {
    const rim = 2 * PI * (Math.sqrt(nE * cellOf(E) / PI) + S.r);
    return Math.min(n, Math.max(1, M.wrap * rim / P.slot * P.ranks));
  }
  return Math.min(n, Math.max(1, width * P.band));
}

/**
 * duel(a, b) -> { winner: 0|1|-1, left: [hp share 0..1, …], t }
 * a, b: unitStats(...) with count (count may be fractional while balancing: the last unit is then wounded)
 */
export function duel(a, b, M = MODEL) {
  const S = [a, b], P = [prep(a, b, M), prep(b, a, M)];
  const H0 = [a.hp * a.count, b.hp * b.count], H = H0.slice();
  const width = Math.max(formationWidth(a), formationWidth(b));
  let d = Math.min(160, Math.max(44, Math.max(a.range, b.range) * 1.25 + 26)) + a.r + b.r, t = 0;
  const n = [0, 0], hits = [0, 0], D = [0, 0];
  while (H[0] > 0 && H[1] > 0 && t < M.tMax) {
    n[0] = Math.ceil(H[0] / a.hp - 1e-9); n[1] = Math.ceil(H[1] / b.hp - 1e-9);
    // enemy units hit per second by each side (only once it is in range)
    for (let k = 0; k < 2; k++) hits[k] = d > P[k].dist + 1e-6 ? 0 : engaged(S[k], P[k], n[k], n[1 - k], S[1 - k], width, M) * P[k].rate * Math.min(P[k].hitsMax, n[1 - k]);
    // close in until the shorter weapon is in range; units under fire are stunned, shoved and tossed
    let close = 0;
    for (let k = 0; k < 2; k++) {
      const h = hits[1 - k] / n[k];
      if (d > P[k].dist) close += S[k].speed * M.walk / (1 + h * P[1 - k].loseMove);
      D[k] = hits[k] * P[k].perHit * M.dt / (1 + (hits[k ^ 1] / n[k]) * P[k ^ 1].loseAtk);
    }
    d = Math.max(Math.min(P[0].dist, P[1].dist), d - close * M.dt);
    H[1] -= D[0]; H[0] -= D[1]; t += M.dt;
  }
  const left = [Math.max(0, H[0]) / H0[0], Math.max(0, H[1]) / H0[1]];
  const winner = H[0] <= 0 && H[1] <= 0 ? -1 : H[1] <= 0 ? 0 : H[0] <= 0 ? 1 : left[0] === left[1] ? -1 : left[0] > left[1] ? 0 : 1;
  return { winner, left, t };
}

/** factor for side k's head count that makes the duel even (< 1: side k is stronger). */
export function evenFactor(a, b, k = 0, M = MODEL) {
  const S = [a, b];
  const wins = m => { const T = S.slice(); T[k] = { ...S[k], count: S[k].count * m }; const r = duel(T[0], T[1], M); return r.winner === k || (r.winner === -1 && r.left[k] >= r.left[1 - k]); };
  let lo = Math.log(1 / 512), hi = Math.log(512);
  if (wins(Math.exp(lo))) return Math.exp(lo);
  if (!wins(Math.exp(hi))) return Math.exp(hi);
  for (let i = 0; i < 22; i++) { const mid = (lo + hi) / 2; if (wins(Math.exp(mid))) hi = mid; else lo = mid; }
  return Math.exp((lo + hi) / 2);
}

export const SHARPNESS = 4;  // how sure the estimate is: p = 1 / (1 + factor^SHARPNESS)
export const HOUSE_EDGE = 0.06;
/** win chance of Blue (0..1). Scales the side with more units (head counts are smooth there). */
export function winChance(a, b, M = MODEL) {
  const k = a.count >= b.count ? 0 : 1;
  const pk = 1 / (1 + Math.pow(evenFactor(a, b, k, M), SHARPNESS));
  return k === 0 ? pk : 1 - pk;
}
/** decimal odds (payout incl. stake per coin) for a win chance p */
export function oddsFor(p, edge = HOUSE_EDGE) {
  const o = (1 - edge) / Math.max(0.01, Math.min(0.99, p));
  return Math.round(Math.min(30, Math.max(1.05, o)) * 100) / 100;
}

/** per-army summary for the betting card */
export function armySummary(S, E) {
  const P = prep(S, E, MODEL);
  return {
    hp: S.hp * S.count,
    dps: S.count * P.rate * P.perHit * Math.min(P.hitsMax, E.count),
    reach: S.melee ? S.reach + S.r : S.range,
    area: S.area || S.aoe > 0,
    mass: S.mass * S.count,
  };
}

/* ---------------- matchup roller ---------------- */
export const BET_CAP = 8000;     // units per side (keeps a bet battle short and smooth)
export const BET_TOTAL = 10000;  // both sides together
const heft = S => S.hp * (S.dmg / S.cd);
// 2 significant digits: 3.742 -> 3.700, 437 -> 440, 12 stays 12
export function niceCount(n) {
  n = Math.max(1, Math.round(n));
  if (n <= 20) return n;
  const p = Math.pow(10, Math.floor(Math.log10(n)) - 1);
  return Math.round(n / p) * p;
}
const pick = (rng, a) => a[Math.floor(rng() * a.length) % a.length];
const logUniform = (rng, lo, hi) => Math.exp(Math.log(lo) + rng() * (Math.log(hi) - Math.log(lo)));

/**
 * rollMatchup(rng, opts) -> { a: cfg, b: cfg, pBlue, odds: [blue, red], absurd, title }
 *   cfg = [type, count, weapon, size] as used by writeCfg in main.js
 * opts: { types, weapons, recipes?, allowType?(k), allowWeapon?(w, k), forceType?: [team, key], absurdChance? }
 */
export function rollMatchup(rng, opts) {
  const { types, weapons, recipes = {}, allowType = () => true, allowWeapon = () => true, forceType = null, absurdChance = 0.15 } = opts;
  const keys = Object.keys(types).filter(k => !k.startsWith('u_') && allowType(k));
  const serpent = k => { const ty = types[k]; return !!(ty.recipe && recipes[ty.recipe] && recipes[ty.recipe].template === 'serpent'); };
  const wkeys = k => Object.keys(weapons).filter(w => allowWeapon(w, k));
  const nativeW = k => (allowWeapon(types[k].weapon, k) || serpent(k)) ? types[k].weapon : pick(rng, wkeys(k));
  const weaponFor = k => serpent(k) ? 'faeuste' : rng() < 0.7 ? nativeW(k) : pick(rng, wkeys(k).length ? wkeys(k) : [types[k].weapon]);
  const stats = (k, w, s, n) => unitStats(types[k], weapons[w], s, n);
  const base = k => stats(k, types[k].weapon, 1, 1);
  const giants = keys.filter(k => base(k).hp >= 1000 && !serpent(k)).concat(keys.filter(serpent));
  const swarm = keys.filter(k => base(k).hp <= 160 && types[k].r <= 0.6);

  for (let tries = 0; tries < 60; tries++) {
    const absurd = !forceType && rng() < absurdChance && giants.length && swarm.length;
    let side, sideCount, fixed;     // fixed = [key, weapon, size, count] of the anchor army; side = [key, weapon, size] of the balanced one
    if (absurd) {
      const g = pick(rng, giants), w = serpent(g) ? 'faeuste' : nativeW(g);
      const sw = pick(rng, swarm), ww = nativeW(sw);
      const gN = rng() < 0.75 ? 1 : 2 + Math.floor(rng() * 2);
      // grow the giant until the balancing swarm is huge: "1 T-Rex vs. 4.000 Hühner"
      const maxS = serpent(g) ? 2 : Math.max(1, Math.min(8, Math.floor(14 / types[g].r)));
      let best = null;
      for (let s = 1; s <= maxS; s++) {
        const n = 400 * evenFactor(stats(sw, ww, 1, 400), stats(g, w, s, gN), 0);
        if (n >= 1200 && n <= BET_CAP) { best = { s, n }; if (rng() < 0.5) break; }
        if (n > BET_CAP) break;
      }
      if (!best) continue;
      fixed = [g, w, best.s, gN]; side = [sw, ww, 1]; sideCount = best.n;
    } else {
      let k0 = pick(rng, keys), k1 = pick(rng, keys);
      if (forceType) { if (forceType[0] === 0) k0 = forceType[1]; else k1 = forceType[1]; }
      if (k0 === k1 && !forceType) continue;
      const w0 = weaponFor(k0), w1 = weaponFor(k1);
      const size = k => serpent(k) || types[k].r > 1.2 ? 1 : rng() < 0.75 ? 1 : 2 + Math.floor(rng() * 2);
      const s0 = size(k0), s1 = size(k1);
      // anchor = the heavier unit; its count decides the scale of the battle
      const h0 = heft(stats(k0, w0, s0, 1)), h1 = heft(stats(k1, w1, s1, 1));
      const ai = forceType ? 1 - forceType[0] : h0 >= h1 ? 0 : 1;
      const A = ai === 0 ? [k0, w0, s0] : [k1, w1, s1], B = ai === 0 ? [k1, w1, s1] : [k0, w0, s0];
      const hpA = stats(A[0], A[1], A[2], 1).hp;
      const nA = Math.round(hpA >= 2500 ? logUniform(rng, 1, 40) : hpA >= 500 ? logUniform(rng, 5, 220) : logUniform(rng, 40, 2500));
      const guess = Math.max(1, nA * Math.sqrt(heft(stats(A[0], A[1], A[2], 1)) / heft(stats(B[0], B[1], B[2], 1))));
      const n = guess * evenFactor(stats(B[0], B[1], B[2], guess), stats(A[0], A[1], A[2], nA), 0);
      fixed = [...A, nA]; side = B; sideCount = n;
      fixed.team = ai; // a forced type (AI unit) is always the balanced side
    }
    // a little jitter so the odds are not always 50:50, then round to a count that reads well
    const jit = Math.exp((rng() - 0.5) * 0.5);
    const nB = niceCount(sideCount * jit), nF = niceCount(fixed[3]);
    if (nB < 1 || nB > BET_CAP || nB + nF > BET_TOTAL) continue;
    const ft = absurd ? (rng() < 0.5 ? 0 : 1) : fixed.team;
    const cfgF = [fixed[0], nF, fixed[1], fixed[2]], cfgB = [side[0], nB, side[1], side[2]];
    const a = ft === 0 ? cfgF : cfgB, b = ft === 0 ? cfgB : cfgF;
    const pBlue = winChance(stats(a[0], a[2], a[3], a[1]), stats(b[0], b[2], b[3], b[1]));
    if (pBlue < 0.2 || pBlue > 0.8) continue;  // close fights only
    return { a, b, pBlue, odds: [oddsFor(pBlue), oddsFor(1 - pBlue)], absurd: !!absurd, title: matchTitle(types, a, b) };
  }
  // fallback that is always fair: mirror match
  const k = keys.includes('ritter') ? 'ritter' : keys[0], w = types[k].weapon;
  const a = [k, 500, w, 1], b = [k, 500, w, 1];
  return { a, b, pBlue: 0.5, odds: [oddsFor(0.5), oddsFor(0.5)], absurd: false, title: matchTitle(types, a, b) };
}
/**
 * rollFromPool(rng, pool, opts) -> matchup like rollMatchup plus `seed`, or null when nothing in the pool is allowed.
 * The pool (src/content/betPool.js, built by server/build-bet-pool.js) holds matchups fought in the real simulation with
 * a fixed set of seeds. The bet battle runs with one of those seeds picked at random, so the win chance shown
 * (wins / seeds) is exact: no strategy beats the house edge. opts: { types, weapons, allowType?(k), allowWeapon?(w, k) }
 */
export function rollFromPool(rng, pool, opts) {
  const { types, weapons, allowType = () => true, allowWeapon = () => true } = opts;
  if (!pool || !Array.isArray(pool.entries) || !Array.isArray(pool.seeds) || !pool.seeds.length) return null;
  const ok = c => types[c[0]] && weapons[c[2]] && allowType(c[0]) && allowWeapon(c[2], c[0]);
  const list = pool.entries.filter(e => ok(e.a) && ok(e.b) && e.variants && e.variants.length);
  if (!list.length) return null;
  const e = list[Math.floor(rng() * list.length) % list.length], v = e.variants[Math.floor(rng() * e.variants.length) % e.variants.length];
  const j = Math.floor(rng() * pool.seeds.length) % pool.seeds.length;
  const a = e.a.slice(), b = e.b.slice(); (e.k ? b : a)[1] = v.n;
  const pBlue = v.w.filter(w => w === 0).length / v.w.length;
  return { a, b, pBlue, odds: [oddsFor(pBlue), oddsFor(1 - pBlue)], absurd: !!e.absurd, title: matchTitle(types, a, b), seed: pool.seeds[j], expect: v.w[j] };
}
const nfDE = new Intl.NumberFormat('de-DE');
export function armyLabel(types, cfg) {
  const ty = types[cfg[0]], n = cfg[1];
  return `${nfDE.format(n)} ${n === 1 ? ty.name : ty.plural}${cfg[3] > 1 ? ` ×${cfg[3]}` : ''}`;
}
export const matchTitle = (types, a, b) => `${armyLabel(types, a)} vs. ${armyLabel(types, b)}`;

/* ---------------- wallet ---------------- */
export const START_COINS = 1000;
export const MIN_COINS = 100;
export const freshWallet = () => ({ v: 1, coins: START_COINS, streak: 0, bestStreak: 0, record: START_COINS, wins: 0, losses: 0 });
export function loadWallet(raw) {
  const w = freshWallet();
  let o = null;
  try { o = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { o = null; }
  if (!o || typeof o !== 'object') return w;
  for (const k of Object.keys(w)) if (k !== 'v' && Number.isFinite(o[k]) && o[k] >= 0) w[k] = Math.floor(o[k]);
  w.coins = Math.max(MIN_COINS, w.coins);
  w.record = Math.max(w.record, w.coins);
  return w;
}
/** stake: whole coins, at least 1, at most the balance */
export const clampStake = (w, stake) => Math.max(1, Math.min(w.coins, Math.floor(+stake || 0)));
/** take the stake out of the wallet when the bet is placed (a reload mid-battle forfeits it) */
export function placeBet(w, stake) { const s = clampStake(w, stake); return { wallet: { ...w, coins: w.coins - s }, stake: s }; }
/**
 * settle a placed bet: winner = 0/1/-1 (draw refunds), pick = team bet on, odds = decimal odds at placement.
 * Returns { wallet, payout, net, rescued } — the balance never stays below MIN_COINS.
 */
export function settleBet(w, { stake, pick, odds }, winner) {
  const n = { ...w };
  let payout = 0;
  if (winner === -1) payout = stake;
  else if (winner === pick) { payout = Math.round(stake * odds); n.wins++; n.streak++; n.bestStreak = Math.max(n.bestStreak, n.streak); }
  else { n.losses++; n.streak = 0; }
  n.coins += payout;
  const rescued = n.coins < MIN_COINS;
  if (rescued) n.coins = MIN_COINS;
  n.record = Math.max(n.record, n.coins);
  return { wallet: n, payout, net: payout - stake, rescued };
}
